import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core'
import {catchError, forkJoin, map, Observable, of, Subscription, take, tap} from 'rxjs'
import {UserPreferencesData} from '../models/user-preferences-data'
import {UserPreferencesHttpService} from '../services/user-preferences-http.service'
import {ToastrService} from 'ngx-toastr'
import {ActivatedRoute, Router} from '@angular/router'
import {createDropDownItems} from '../setting-types/boolean-setting'
import {
  ChangeTrackedValue,
  IDropDownSettingDescriptor,
  IPeriodicEmailSettingsTuple,
  ISettingsPresentation
} from '../setting-types/common'
import {PeriodicEmailSettingsComponent} from './periodic-email-settings/periodic-email-settings.component'
import {DisplayMode} from './periodic-email-settings/common'
import {IPeriodicEmailSettings} from '../setting-types/periodic-email-settings'
import {UserPreferencesService} from '../services/user-preferences.service'

@Component({
  templateUrl: './user-preferences.component.html'
})
export class UserPreferencesComponent implements OnInit, OnDestroy {

  settingsPresentation$: Observable<ISettingsPresentation>
  saveButtonEnabled: boolean
  queryParamsSubscription: Subscription
  returnUrl: string
  readonly sendEmailOnFormAssignmentDescriptor: IDropDownSettingDescriptor = {
    controlId: "send-email-on-form-assignment",
    controlLabelText: "Receive email notification when forms are assigned to you",
    options: null,
    value: null,
    disabled: false,
    hidden: false
  }
  readonly displayNonCurrentManagersDescriptor: IDropDownSettingDescriptor = {
    controlId: "display-non-current-managers",
    controlLabelText: "Display non-current managers on dashboards",
    options: null,
    value: null,
    disabled: false,
    hidden: false
  }
  readonly skipListerPageDescriptor: IDropDownSettingDescriptor = {
    controlId: "skip-lister-page",
    controlLabelText: "Skip lister page for single records",
    options: null,
    value: null,
    disabled: false,
    hidden: false
  }
  readonly displayObsoleteUnitsDescriptor: IDropDownSettingDescriptor = {
    controlId: "display-obsolete-units",
    controlLabelText: "Display obsolete units",
    options: null,
    value: null,
    disabled: false,
    hidden: false
  }
  readonly formCompletionNavigationDescriptor: IDropDownSettingDescriptor = {
    controlId: "form-completion-navigation",
    controlLabelText: "Where to navigate to on form submission",
    options: null,
    value: null,
    disabled: false,
    hidden: false
  }
  readonly displayNonCurrentLeasesAndTenantsDescriptor: IDropDownSettingDescriptor = {
    controlId: "display-non-current-leases-and-tenants",
    controlLabelText: "Display non-current leases and tenants on dashboards",
    options: null,
    value: null,
    disabled: false,
    hidden: false
  }
  readonly displayNonCurrentHeadLeasesAndLandlordsDescriptor: IDropDownSettingDescriptor = {
    controlId: "display-non-current-head-leases-and-landlords",
    controlLabelText: "Display non-current head leases and landlords on dashboards",
    options: null,
    value: null,
    disabled: false,
    hidden: false
  }

  DM = DisplayMode;
  @ViewChild('periodicEmailSettings') readonly periodicEmailSettingsComponent: PeriodicEmailSettingsComponent;
  periodicEmailSettings: IPeriodicEmailSettings

  constructor(private readonly userPreferencesHttpService: UserPreferencesHttpService,
              private readonly userPreferenceService: UserPreferencesService,
              private readonly route: ActivatedRoute,
              private readonly router: Router,
              private readonly toastrService: ToastrService) {
  }

  ngOnInit() {
    this.queryParamsSubscription =
      this.route.queryParams.subscribe(params => {
        this.returnUrl = params.returnUrl
      })
    const getSystemDefaults = this.userPreferencesHttpService.getUserPreferencesSystemDefaults()
      .pipe(
        take(1),
        catchError(e => {
          this.toastrService.error(`Error getting User Preferences System Defaults ${e.status} ${e.message}`)
          this.closeDialog()
          return of(null)
        }))
    const getUserPreferences = this.userPreferencesHttpService.getUserPreferences()
      .pipe(
        take(1),
        catchError(e => {
          this.toastrService.error(`Error getting User Preferences ${e.status} ${e.message}`)
          this.closeDialog()
          return of(null)
        }))
    this.settingsPresentation$ = forkJoin([getSystemDefaults, getUserPreferences])
      .pipe(
        map(([systemDefaults, userPreferences]) => {

          this.sendEmailOnFormAssignmentDescriptor.options =
            createDropDownItems(systemDefaults.SendEmailOnFormAssignment)
          this.sendEmailOnFormAssignmentDescriptor.value =
            new ChangeTrackedValue<boolean>(userPreferences.SendEmailOnFormAssignment)

          this.displayNonCurrentManagersDescriptor.options =
            createDropDownItems(systemDefaults.DisplayNonCurrentManagers)
          this.displayNonCurrentManagersDescriptor.value =
            new ChangeTrackedValue<boolean>(userPreferences.DisplayNonCurrentManagers)

          this.skipListerPageDescriptor.options =
            createDropDownItems(systemDefaults.SkipListerPage)
          this.skipListerPageDescriptor.value =
            new ChangeTrackedValue<boolean>(userPreferences.SkipListerPage)

          this.periodicEmailSettings = userPreferences.PeriodicEmailSettings

          this.displayObsoleteUnitsDescriptor.options =
            createDropDownItems(systemDefaults.DisplayObsoleteUnits)
          this.displayObsoleteUnitsDescriptor.value =
            new ChangeTrackedValue<boolean>(userPreferences.DisplayObsoleteUnits)

          this.displayNonCurrentLeasesAndTenantsDescriptor.options =
            createDropDownItems(systemDefaults.DisplayNonCurrentLeasesAndTenants)
          this.displayNonCurrentLeasesAndTenantsDescriptor.value =
            new ChangeTrackedValue<boolean>(userPreferences.DisplayNonCurrentLeasesAndTenants)

          this.displayNonCurrentHeadLeasesAndLandlordsDescriptor.options =
            createDropDownItems(systemDefaults.DisplayNonCurrentHeadLeasesAndLandlords)
          this.displayNonCurrentHeadLeasesAndLandlordsDescriptor.value =
            new ChangeTrackedValue<boolean>(userPreferences.DisplayNonCurrentHeadLeasesAndLandlords)

          this.formCompletionNavigationDescriptor.options =
            [
              {value: 0, label: 'Show Options'},
              {value: 1, label: 'Navigate To My Activities'},
              {value: 2, label: 'Navigate To New Activities'}
            ];

          this.formCompletionNavigationDescriptor.value =
            new ChangeTrackedValue<string>(userPreferences.FormCompletionNavigation)

          return {
            SendEmailOnFormAssignment: this.sendEmailOnFormAssignmentDescriptor,
            DisplayNonCurrentManagers: this.displayNonCurrentManagersDescriptor,
            SkipListerPage: this.skipListerPageDescriptor,
            DisplayObsoleteUnits: this.displayObsoleteUnitsDescriptor,
            PeriodicEmailSettingsTuple: <IPeriodicEmailSettingsTuple>{
              SystemDefaults: systemDefaults.PeriodicEmailSettings,
              UserPreferences: userPreferences.PeriodicEmailSettings,
            },
            FormCompletionNavigation: this.formCompletionNavigationDescriptor,
            DisplayNonCurrentLeasesAndTenants: this.displayNonCurrentLeasesAndTenantsDescriptor,
            DisplayNonCurrentHeadLeasesAndLandlords: this.displayNonCurrentHeadLeasesAndLandlordsDescriptor,
          }
        }))
  }

  closeDialog() {
    if (this.returnUrl.startsWith('http')) {
      // full re-load because the address originated from mojo forms
      window.location.href = this.returnUrl
    } else {
      // no need to re-load as dialog opened from mojo platform
      this.router.navigateByUrl(this.returnUrl)
    }
  }

  cancel() {
    this.closeDialog()
  }

  save() {
    let requestData = new UserPreferencesData(
      this.sendEmailOnFormAssignmentDescriptor.value.current,
      this.displayNonCurrentManagersDescriptor.value.current,
      this.skipListerPageDescriptor.value.current,
      this.displayObsoleteUnitsDescriptor.value.current,
      this.periodicEmailSettings,
      this.formCompletionNavigationDescriptor.value.current,
      this.displayNonCurrentLeasesAndTenantsDescriptor.value.current,
      this.displayNonCurrentHeadLeasesAndLandlordsDescriptor.value.current)

    const saveUserPreferences = this.userPreferencesHttpService.setUserPreferences(requestData)
      .pipe(
        take(1),
        catchError(e => {
          this.toastrService.error(`Error saving User Preferences ${e.status} ${e.message}`)
          this.closeDialog()
          return of(null)
        }),
        tap(_ => {
          this.toastrService.success('User Preferences saved successfully')
          this.closeDialog()
          this.userPreferenceService.loadUserPreferences();
        }))
    saveUserPreferences.subscribe()
  }

  onDropDownChanged(e) {
    this.saveButtonEnabled = this.hasControlValuesChanged()
  }

  periodicEmailSettingsChanged(e) {
    this.saveButtonEnabled = this.hasControlValuesChanged()
    this.periodicEmailSettings = e
  }

  private hasControlValuesChanged(): boolean {
    return this.sendEmailOnFormAssignmentDescriptor.value.changed() ||
      this.displayNonCurrentManagersDescriptor.value.changed() ||
      this.skipListerPageDescriptor.value.changed() ||
      this.displayObsoleteUnitsDescriptor.value.changed() ||
      this.formCompletionNavigationDescriptor.value.changed() ||
      (this.periodicEmailSettingsComponent && this.periodicEmailSettingsComponent.changed()) ||
      this.displayNonCurrentLeasesAndTenantsDescriptor.value.changed() ||
      this.displayNonCurrentHeadLeasesAndLandlordsDescriptor.value.changed()
  }

  ngOnDestroy() {
    if (this.queryParamsSubscription != null)
      this.queryParamsSubscription.unsubscribe()
  }
}
